import { useNavigate } from 'react-router-dom';
import { Loader2, LogOut, ShieldAlert } from 'lucide-react';

import { useActingAs } from '@/hooks/useActingAs';
import { useClearActiveCustomer } from '@/hooks/useMemberships';

/**
 * ActingAsBar — the loud, always-present reminder that a superadmin is
 * operating inside a customer's account. Part of the act-as safety net: with
 * payments live, a staff member must never forget which customer their next
 * order or charge will land on. Rendered across all customer-context pages
 * (not /admin, where the acting context is irrelevant).
 *
 * "Exit" clears the acting context server-side (active_customer_id → NULL) so
 * there is no sticky impersonation left behind, then returns to the admin
 * quick-order launchpad.
 */
export function ActingAsBar() {
  const { isActingAs, customerName, customerActive } = useActingAs();
  const clear = useClearActiveCustomer();
  const navigate = useNavigate();

  if (!isActingAs) return null;

  async function exit() {
    try {
      await clear.mutateAsync();
    } finally {
      navigate('/admin/quick-order');
    }
  }

  return (
    <div
      role="status"
      className="flex items-center gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-amber-950 md:px-6"
    >
      <ShieldAlert size={18} className="shrink-0 text-amber-700" aria-hidden />
      <p className="min-w-0 flex-1 text-sm leading-tight">
        <span className="font-bold">Acting as {customerName}</span>
        {customerActive === false && (
          <span className="ml-1 font-semibold">(deactivated)</span>
        )}
        <span className="hidden sm:inline">
          {' '}— orders and payments you make will be filed to this customer.
        </span>
      </p>
      <button
        type="button"
        onClick={() => void exit()}
        disabled={clear.isPending}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-400 bg-white/70 px-3 py-1.5 text-sm font-bold text-amber-900 transition-colors hover:bg-white disabled:opacity-60"
      >
        {clear.isPending ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
        Exit
      </button>
    </div>
  );
}
